import { $applyNodeReplacement, DecoratorNode, EditorConfig, LexicalEditor, SerializedLexicalNode, Spread } from "lexical";
import { ImageMixin, ImageMixinPayload, ImagePayload, SerializedImage } from "../mixins/ImageMixin";

import React, { Suspense } from "react";
import { StickyMixin, StickyMixinPayload, StickyPayload, SerializedSticky } from "../mixins/StickyMixin";
import { ContainerMixin, ContainerMixinPayload, ContainerPayload, SerializedContainer } from "../mixins/ContainerMixin";

const StickyComponent = React.lazy(() => import('../mixins/components/StickyComponent'));
const ImageComponent = React.lazy(() => import('../mixins/components/ImageComponent'));

export interface StickyImagePayload extends StickyPayload, ContainerPayload, ImagePayload {
  key?: string;
}

export type SerializedStickyImageNode = Spread<
  SerializedSticky & SerializedContainer & SerializedImage,
  SerializedLexicalNode
>;

class StickyImageBase extends DecoratorNode<JSX.Element> implements StickyMixinPayload, ContainerMixinPayload, ImageMixinPayload {
  __src!: string;
  __altText!: string;
  __x!: number;
  __y!: number;
  __z!: number | 'auto';
  __width!: number;
  __height!: number;
  __isSticky!: boolean;

  constructor(
    src: string,
    altText: string,
    x?: number,
    y?: number,
    z?: number | 'auto',
    width?: number,
    height?: number,
    key?: string,
  ) {
    super(key);
    this.__x = x || 15;
    this.__y = y || 30;
    this.__z = z || 'auto';
    this.__width = width || 0;
    this.__height = height || 0;
    this.__src = src || '';
    this.__altText = altText || '';
  }

  createDOM(config: EditorConfig, editor: LexicalEditor): HTMLElement {
    const element = document.createElement('div');
    element.className = 'sticky-image';
    return element;
  }

  updateDOM(prevNode: unknown, dom: HTMLElement, config: EditorConfig) {
    return false;
  }

  exportJSON(): SerializedStickyImageNode {
    const self = this.getLatest();
    return {
      type: StickyImageNode.getType(),
      version: 1,
      src: self.__src,
      altText: self.__altText,
      x: self.__x,
      y: self.__y,
      z: self.__z,
      width: self.__width,
      height: self.__height,
    }
  }
}

export class StickyImageNode extends ImageMixin(StickyMixin(ContainerMixin(StickyImageBase))) {
  static getType() {
    return 'sticky-image';
  }

  static clone(node: StickyImageNode) {
    return new StickyImageNode(
      node.__src,
      node.__altText,
      node.__x,
      node.__y,
      node.__z,
      node.__width,
      node.__height,
      node.__key,
    );
  }

  static importJSON(json: SerializedStickyImageNode) {
    const { x, y, z, width, height, src, altText } = json;
    const node = $createStickyImageNode(src, altText, x, y, z, width, height);
    return node;
  }

  decorate(): JSX.Element {
    return (
      <Suspense fallback={null}>
        <StickyComponent
          nodeKey={this.__key}
          x={this.__x}
          y={this.__y}
          z={this.__z}
          width={this.__width}
          height={this.__height}
        >
          <ImageComponent
            nodeKey={this.__key}
            src={this.__src}
            altText={this.__altText}
          />
        </StickyComponent>
      </Suspense>
    )
  }
}

export function $createStickyImageNode(
  src: string,
  altText: string,
  x?: number,
  y?: number,
  z?: number | 'auto',
  width?: number,
  height?: number,
  key?: string,
) {
  return $applyNodeReplacement(new StickyImageNode(src, altText, x, y, z, width, height, key));
}

export function $isStickyImageNode(node: unknown): node is StickyImageNode {
  return node instanceof StickyImageNode;
}